import React, { useContext } from 'react';
import { Typography, Button } from 'antd';
import {
  UserOutlined,
  GiftOutlined,
  FileTextOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { orDash } from '../utils/format';
import { FaTicketAlt } from 'react-icons/fa';
import { IInvoiceDetailsUser } from '../interfaces';
import { formatCpf, formatPhone } from '../../../utils/Masks';
import { poeValorComPontosEmMil } from '../../../utils/Conversores';
import { ThemeContext } from '../../../contexts/ThemeContext';

const { Text } = Typography;

interface UserCardProps {
  user: IInvoiceDetailsUser | undefined;
  onViewUser?: (id: number) => void;
}

export const UserCard: React.FC<UserCardProps> = ({ user, onViewUser }) => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';

  if (!user) {
    return <></>;
  }

  const cardBg = isDark ? '#141414' : '#ffffff';
  const itemBg = isDark ? '#1f1f1f' : '#f8fafc';
  const itemBorder = isDark ? '#2a2a2a' : '#eaedf1';
  const labelColor = isDark ? '#8c8c8c' : '#64748b';
  const textColor = isDark ? '#f0f0f0' : '#1e293b';

  const addressText = [
    user.street,
    user.number,
    user.neighborhood,
    user.complement,
    user.city ? `${user.city}${user.uf ? ` - ${user.uf}` : ''}` : undefined,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div
      style={{
        background: cardBg,
        borderRadius: 14,
        border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
        boxShadow: isDark
          ? '0 4px 20px rgba(0, 0, 0, 0.35)'
          : '0 2px 10px rgba(0, 0, 0, 0.03)',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
          background: isDark ? '#1a1a1a' : '#fafafa',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: isDark ? 'rgba(22, 119, 255, 0.15)' : 'rgba(22, 119, 255, 0.1)',
              color: '#1677ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
            }}
          >
            <UserOutlined />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: textColor }}>
              Usuário Participante
            </div>
            <div style={{ fontSize: 12, color: labelColor }}>
              Dados do cidadão cadastrado na nota
            </div>
          </div>
        </div>

        <Button
          type="primary"
          icon={<UserOutlined />}
          disabled={!user.id}
          onClick={() => user.id && onViewUser?.(user.id)}
          style={{
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 500,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          Ver usuário
        </Button>
      </div>

      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 16, flex: 1 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 10,
          }}
        >
          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: labelColor, marginBottom: 2 }}>ID do Usuário</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Text strong style={{ fontSize: 14, color: textColor }}>#{user.id}</Text>
            </div>
          </div>

          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: labelColor, marginBottom: 2 }}>Nome Completo</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: textColor }}>
              {orDash(user.name)}
            </div>
          </div>
          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: labelColor, marginBottom: 2 }}>CPF</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: textColor }}>
              {user.cpf ? formatCpf(user.cpf) : '--'}
            </div>
          </div>

          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: labelColor, marginBottom: 2 }}>Telefone</div>
            <div style={{ fontSize: 13, fontWeight: 500, color: textColor }}>
              {user.phone ? formatPhone(user.phone) : '--'}
            </div>
          </div>

          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '10px 12px', gridColumn: '1 / -1' }}>
            <div style={{ fontSize: 11, color: labelColor, marginBottom: 2 }}>E-mail</div>
            <div style={{ fontSize: 13, color: textColor }}>
              {orDash(user.email)}
            </div>
          </div>

          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '10px 12px', gridColumn: '1 / -1' }}>
            <div style={{ fontSize: 11, color: labelColor, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
              <EnvironmentOutlined /> Endereço Completo
            </div>
            <div style={{ fontSize: 12, color: textColor }}>
              {addressText || '--'}
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          <div
            style={{
              background: itemBg,
              borderRadius: 8,
              border: `1px solid ${itemBorder}`,
              padding: '12px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 8,
                background: isDark ? 'rgba(22, 119, 255, 0.2)' : '#e6f4ff',
                color: '#1677ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                flexShrink: 0,
              }}
            >
              <FileTextOutlined />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: labelColor }}>Notas</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: textColor, lineHeight: 1.2 }}>
                {poeValorComPontosEmMil(user.total_invoices ?? 0)}
              </div>
            </div>
          </div>

          <div
            style={{
              background: itemBg,
              borderRadius: 8,
              border: `1px solid ${itemBorder}`,
              padding: '12px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 8,
                background: isDark ? 'rgba(82, 196, 26, 0.2)' : '#f6ffed',
                color: '#52c41a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                flexShrink: 0,
              }}
            >
              <FaTicketAlt />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: labelColor }}>Bilhetes</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: textColor, lineHeight: 1.2 }}>
                {poeValorComPontosEmMil(user.total_tickets ?? 0)}
              </div>
            </div>
          </div>

          <div
            style={{
              background: itemBg,
              borderRadius: 8,
              border: `1px solid ${itemBorder}`,
              padding: '12px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 8,
                background: isDark ? 'rgba(250, 140, 22, 0.2)' : '#fff7e6',
                color: '#fa8c16',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                flexShrink: 0,
              }}
            >
              <GiftOutlined />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: labelColor }}>Sorteios</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: textColor, lineHeight: 1.2 }}>
                {poeValorComPontosEmMil(user.total_raffles ?? 0)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserCard;

